import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, map, tap } from 'rxjs/operators';

import { Animations, AutoSub, AutoSubOnDestroy, ClickOutsideDirectiveModule, MoteService, MoteType, MoteTypeIconMap, ProjectService, SvgIconModule, } from '@motes-sdk';
import { MapOverlayService } from '../../../../services/map-overlay.service';

/** Map of additional information related to of tour types */
const TourTypeInfoMap: { [K in MoteType]?: { title: string, icon: string, description: string; available: boolean; } } = {
    [MoteType.COLLECTION]: {
        title: $localize`collection`,
        icon: MoteTypeIconMap[MoteType.COLLECTION],
        description: $localize`Create themed collections of stories for your users to explore`,
        available: true,
    },
    [MoteType.ROUTE]: {
        title: $localize`route`,
        icon: MoteTypeIconMap[MoteType.ROUTE],
        description: $localize`Unfold your stories as a linear experience with a start and end point`,
        available: true,
    },
    [MoteType.QUEST]: {
        title: $localize`quest`,
        icon: MoteTypeIconMap[MoteType.QUEST],
        description: $localize`Answer questions correctly to unlock the subsequent path`,
        available: false,
    },
    [MoteType.SCHEDULE]: {
        title: $localize`schedule`,
        icon: MoteTypeIconMap[MoteType.SCHEDULE],
        description: $localize`A collection of events taking place at specific points in time`,
        available: false,
    },
};

/**
 * A menu for changing the type of the active tour
 */
@AutoSubOnDestroy()
@Component({
    selector: 'select-tour-type',
    standalone: true,
    imports: [CommonModule, FormsModule, SvgIconModule, ClickOutsideDirectiveModule],
    templateUrl: './select-tour-type.component.html',
    styleUrls: ['./select-tour-type.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeIn(200)],
})
export class SelectTourTypeComponent implements OnInit {

    /** Key for the active tour */
    public activeTourKey: string;
    /** The type of the active tour */
    public activeTourType: MoteType;
    /** Existing tour types */
    public TourType = MoteType;
    /** Map of additional information related to of tour types */
    public TourTypeInfoMap = TourTypeInfoMap;
    /** Message shown if the type property on the active tour does not exist in the `TourDescriptions` map */
    public missingTourTypeMessage: string = $localize`No tour type selected`;
    /** Flag to determine whether or not to display the tour type options in the UI */
    public isOptionsVisible: boolean = false;

    constructor(private projectService: ProjectService, private moteService: MoteService, private mapOverlayService: MapOverlayService) { }

    ngOnInit(): void {
        AutoSub(this).reg['activeToolSub'] = this.mapOverlayService.watchActiveTool().subscribe((activeTool) => {
            if(activeTool != 'select-tour-type') this.isOptionsVisible = false;
        })

        AutoSub(this).reg['activeTourSub'] = this.projectService.watchActiveTourDetails()
            .pipe(
                // Store the key of the active tour in a member variable
                tap((tourDetails) => this.activeTourKey = tourDetails?.tour?.key),
                map((tourDetails) => tourDetails?.tour?.type),
                // Prevent setting the same value repeatedly
                filter((tourType) => tourType != this.activeTourType)
            )
            .subscribe((tourType) => {
                this.activeTourType = tourType;
            });
    }

    /**
     * Change the type of the active tour
     * @param type the `MoteType` to set
     */
    public changeTourType(type: any) {
        // Prevent setting an unavailable tour type
        if(!(type in this.TourTypeInfoMap && this.TourTypeInfoMap[type].available)) return;
        // If the tour type has changed, update the database
        if(type != this.activeTourType) this.moteService.setType(this.activeTourKey, type);
        // Hide options
        this.toggleOpen(false);
    }

    public toggleOpen(isOptionsVisible: boolean = !this.isOptionsVisible) {
        this.isOptionsVisible = isOptionsVisible;
        this.mapOverlayService.updateActiveTool(this.isOptionsVisible, 'select-tour-type');
    }
}
